import { CheckCircle2, ShieldAlert, XCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface Props {
  hitlRequestId: string;
  toolName: string;
  toolInput: Record<string, unknown>;
  /**
   * Disparado quando user clica Aprovar/Rejeitar. O caller (ChatStream)
   * eh quem abre o SSE de ``/hitl-requests/{id}/decide`` -- a resposta
   * eh stream, nao JSON, entao o banner nao pode chamar api.POST.
   */
  onDecide?: (payload: {
    decision: "approve" | "reject";
    comment?: string;
    tool_input_override?: Record<string, unknown>;
  }) => Promise<void> | void;
  /** Se ja foi decidido (status != pending), desabilita botoes. */
  pending?: boolean;
  /**
   * Quando true, mostra o banner como "decidido" -- esconde os botoes
   * e exibe um badge com a decisao. Usado para preservar o registro
   * historico do HITL apos a aprovacao/rejeicao.
   */
  resolved?: boolean;
  /** Decisao final (so usado quando ``resolved=true``). */
  decision?: "approve" | "reject";
  /** Comentario opcional na rejeicao. */
  decisionComment?: string;
}

export function HitlBanner({
  hitlRequestId,
  toolName,
  toolInput,
  onDecide,
  pending = false,
  resolved = false,
  decision,
  decisionComment,
}: Props) {
  const { t } = useTranslation("hitl");
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [comment, setComment] = useState("");
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [overrideText, setOverrideText] = useState(
    JSON.stringify(toolInput, null, 2),
  );

  async function handleApprove() {
    if (!onDecide) return;
    setBusy(true);
    try {
      let parsedOverride: Record<string, unknown> | undefined;
      if (overrideEnabled) {
        try {
          parsedOverride = JSON.parse(overrideText);
        } catch {
          toast.error("Args inválido (JSON malformado).");
          setBusy(false);
          return;
        }
      }
      await onDecide({
        decision: "approve",
        tool_input_override: parsedOverride,
      });
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    if (!onDecide) return;
    if (!comment.trim()) {
      toast.error(t("banner.commentRequiredOnReject"));
      return;
    }
    setBusy(true);
    try {
      await onDecide({ decision: "reject", comment });
    } finally {
      setBusy(false);
    }
  }

  const disabled = busy || pending;

  // Cores: amarelo quando pendente, verde aprovado, vermelho rejeitado.
  const containerClass = resolved
    ? decision === "approve"
      ? "border-green-500/40 bg-green-500/10"
      : "border-red-500/40 bg-red-500/10"
    : "border-yellow-500/40 bg-yellow-500/10";

  return (
    <div
      className={`my-2 rounded-md border-2 p-3 ${containerClass}`}
    >
      <div className="flex items-center gap-2">
        {resolved && decision === "approve" ? (
          <CheckCircle2 className="h-4 w-4 text-green-700" />
        ) : resolved && decision === "reject" ? (
          <XCircle className="h-4 w-4 text-red-700" />
        ) : (
          <ShieldAlert className="h-4 w-4 text-yellow-700" />
        )}
        <span className="text-sm font-medium">{t("banner.title")}</span>
        {resolved ? (
          <Badge
            variant="outline"
            className={
              decision === "approve"
                ? "border-green-500/50 text-green-800"
                : "border-red-500/50 text-red-800"
            }
          >
            {decision === "approve" ? "Aprovado" : "Rejeitado"}
          </Badge>
        ) : null}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {t("banner.toolPrefix")} <code className="font-mono">{toolName}</code>
      </p>

      <div className="mt-2">
        <details>
          <summary className="cursor-pointer text-xs text-muted-foreground">
            {t("banner.viewArgs")}
          </summary>
          <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted/50 p-2 font-mono text-xs">
            {JSON.stringify(toolInput, null, 2)}
          </pre>
        </details>
      </div>

      {resolved && decisionComment ? (
        <p className="mt-2 text-xs italic text-muted-foreground">
          Comentário: {decisionComment}
        </p>
      ) : null}

      {!resolved ? (
        <>
          <div className="mt-2 flex items-center gap-2">
            <input
              id={`override-${hitlRequestId}`}
              type="checkbox"
              checked={overrideEnabled}
              onChange={(e) => setOverrideEnabled(e.target.checked)}
              disabled={disabled}
            />
            <Label htmlFor={`override-${hitlRequestId}`} className="text-xs">
              {t("banner.editArgs")}
            </Label>
          </div>
          {overrideEnabled ? (
            <Textarea
              value={overrideText}
              onChange={(e) => setOverrideText(e.target.value)}
              rows={6}
              className="mt-1 font-mono text-xs"
              disabled={disabled}
            />
          ) : null}

          {rejectMode ? (
            <div className="mt-2 space-y-1">
              <Label
                htmlFor={`comment-${hitlRequestId}`}
                className="text-xs"
              >
                {t("banner.commentLabel")} *
              </Label>
              <Textarea
                id={`comment-${hitlRequestId}`}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                placeholder={t("banner.commentRequiredOnReject")}
                disabled={disabled}
              />
            </div>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={handleApprove} disabled={disabled}>
              {busy
                ? "..."
                : overrideEnabled
                  ? t("banner.approveAndEdit")
                  : t("banner.approve")}
            </Button>
            {!rejectMode ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRejectMode(true)}
                disabled={disabled}
              >
                {t("banner.reject")}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="destructive"
                onClick={handleReject}
                disabled={disabled}
              >
                {t("banner.reject")}
              </Button>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
